import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useStateValue } from "../../../store/StateProvider";
import { getSellerCreationErrors } from "../../../utils/validator";
import MapSelector from "../Address/GoogleMap";
import AddressAutocomplete from "../Address/AddressAutocomplete";
import constants from "../../../config/constants";
import { actionTypes } from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import intlConstants from "../../../config/intlConstants";

const AddressMapField = ({ fieldConfig, showErrors }) => {
  const [{ hiddenFields, formValues, country, confirmedValues, registrationCountry, sellerCreationErrors }] =
    useStateValue();
  const sellerCreationFieldError = getSellerCreationErrors(fieldConfig.id, sellerCreationErrors);
  const [errorMessage, setErrorMessage] = useState(sellerCreationFieldError);
  const [firstLoad, setFirstLoad] = useState(true);
  const restrictedCountry = country == constants.COUNTRY_CODE_INTL ? registrationCountry.code : country;

  const completedData = {
    ...formValues,
    ...confirmedValues,
    country: {
      label:
        country == constants.COUNTRY_CODE_INTL ? registrationCountry.name : intlConstants.COUNTRY_DATA[country].name
    }
  };

  const defaultAddress = constants.DEFAULT_MAP_LOCATION[restrictedCountry];
  const [selectedAddress, setSelectedAddress] = useState({
    location: completedData[fieldConfig.references.coordinates] ?? defaultAddress
  });
  const [inputValue, setInputValue] = useState("");
  const [, dispatch] = useStateValue();
  const { t } = useTranslation();

  const labelClass = `input_label_plain ${fieldConfig.labelClass}`;
  const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const googleMapsLanguage = country == constants.COUNTRY_CODE_INTL ? constants.ENGLISH_LANG : constants.SPANISH_LANG;

  useEffect(() => {
    if (showErrors) {
      validateAddress(inputValue, selectedAddress);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showErrors]);

  useEffect(() => {
    if (formValues[fieldConfig.references.coordinates]) {
      setSelectedAddress({ location: formValues[fieldConfig.references.coordinates] });
    }
  }, [formValues, fieldConfig]);

  const dispatchUpdates = (fieldValue, coordinates) => {
    dispatch({
      type: actionTypes.PRESERVE_GLOBAL_STATE,
      payload: { [fieldConfig.id]: fieldValue }
    });

    if (fieldConfig.references.coordinates) {
      dispatch({
        type: actionTypes.PRESERVE_GLOBAL_STATE,
        payload: { [fieldConfig.references.coordinates]: coordinates ?? null }
      });
    }
  };

  const validateAddress = (input, selectedInput) => {
    if (fieldConfig.required && (input.trim() === "" || selectedAddress === null)) {
      dispatchUpdates(null, null);
      setErrorMessage(t("content.notEmptyAddress"));
      return;
    }

    if (input !== selectedInput) {
      dispatchUpdates(null, null);
      setErrorMessage(t("content.errorValidAddress"));
    }
  };

  const handleOnBlur = (input, selectedInput) => {
    setInputValue(input);
    validateAddress(input, selectedInput);
  };

  const handlePlaceSelect = details => {
    setSelectedAddress({
      address: details.address_components,
      location: details.geometry.location
    });
    dispatchUpdates(details.formatted_address, {
      lat: details.geometry.location.lat(),
      lng: details.geometry.location.lng()
    });

    setErrorMessage(null);
  };

  return (
    <Form.Group
      className={`mb3 ${fieldConfig.containerClass} ${hiddenFields.includes(fieldConfig.id) ? "hidden" : ""}`}
      id={`container_${fieldConfig.id}`}
      controlId={fieldConfig.name}>
      <Form.Label className={labelClass}>{fieldConfig.label}</Form.Label>
      <Form.Label className="label_description">{fieldConfig.labelDescription}</Form.Label>
      <AddressAutocomplete
        restrictedCountry={restrictedCountry}
        apiKey={googleApiKey}
        language={googleMapsLanguage}
        onPlaceSelect={handlePlaceSelect}
        setFirstLoad={setFirstLoad}
        formValues={completedData}
        fieldConfig={fieldConfig}
        handleOnBlur={handleOnBlur}
      />
      <Form.Label
        className={`label_error ${!!errorMessage && (!firstLoad || showErrors || sellerCreationFieldError) ? "show" : "hidden"}`}
        id={`label_error_${fieldConfig.id}`}>
        <span id={`label_error_message_${fieldConfig.id}`}>{errorMessage}</span>
      </Form.Label>
      <MapSelector apiKey={googleApiKey} language={googleMapsLanguage} addressLocation={selectedAddress.location} />
    </Form.Group>
  );
};

export default AddressMapField;
